import { applicationCapabilities, SubCapabilityKey } from "@next-platform/client-common-core/infra/application/capabilities";
import parseRulebookMeterChips from "@next-platform/client-common-core/rulebook/parseRulebookMeterChips";
import { CharacterCardActions, CharacterCardVariant } from "@next-platform/client-common-spicy-ui";

import { Collection } from "@/types/api";
import { HitItem } from "@/types/hits";

import { CharacterCardDropdownActions } from "../types";
import getDropdownActions from "./getHitDropdownActions";

interface IGetHitRulebookConfigProps {
  rulebook: HitItem;
  t: (key: string) => string;
  translateTag: (tag: string) => string;
  isAuthenticated: boolean;
  canAttach?: boolean;
  characterCardActionsHandler: (action: CharacterCardActions | CharacterCardDropdownActions, characterId: string, isNsfw?: boolean) => void;
  /** False until the draft store is scoped to a signed-in account — Clone writes a local draft. */
  isRulebookDraftReady?: boolean;
}

/** Rulebook-index fields beyond the shared HitItem shape (API names stay snake_case). */
export interface IRulebookHitFields {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Typesense index field
  rulebook_id?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Typesense index field
  num_meters?: number;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Typesense index field
  num_rules?: number;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Typesense index field
  num_hidden_meters?: number;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Typesense index field
  meter_chips?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Typesense index field
  num_attached_characters?: number;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Typesense index field
  num_attached_personas?: number;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Typesense index field
  cover_url?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Typesense index field
  cover_is_nsfw?: boolean;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Typesense index field
  moderation_report_status?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Typesense index field
  definition_visible?: boolean;
  visibility?: string;
  status?: string;
}

const getRulebookListingFields = (hit: HitItem & Partial<IRulebookHitFields>) => ({
  num_meters: hit.num_meters || 0,
  num_rules: hit.num_rules ?? 0,
  num_hidden_meters: hit.num_hidden_meters ?? 0,
  meter_chips: parseRulebookMeterChips(hit.meter_chips),
  num_attached_characters: hit.num_attached_characters ?? 0,
  num_attached_personas: hit.num_attached_personas ?? 0,
  ...(hit.definition_visible !== undefined && { definition_visible: hit.definition_visible }),
});

/**
 * Returns the rulebook hit config array based on provided parameters.
 * Rulebooks have no favourites backend and no dedicated share capability — the card exposes
 * Share (public-discovery gated), Report and Block Creator through the dropdown only.
 */
export default function getHitRulebookConfig({
  rulebook,
  t,
  translateTag,
  isAuthenticated,
  canAttach = false,
  characterCardActionsHandler,
  isRulebookDraftReady = false,
}: IGetHitRulebookConfigProps) {
  // Rulebook-index fields the shared HitItem shape doesn't declare.
  const hit = rulebook as HitItem & Partial<IRulebookHitFields>;
  const id = hit.rulebook_id || hit?.id;
  const tags = hit?.tags?.map((tag: string) => translateTag(tag));
  const uniqueKey = hit?.recommId ? `${id}-rec-${hit?.recommId}` : id;

  return {
    id,
    creator_user_id: hit.creator_user_id,
    creator_username: hit.creator_username,
    ...getRulebookListingFields(hit),
    characterTags: tags,
    name: hit.name,
    description: hit.description,
    cover_url: hit.cover_url,
    cover_is_nsfw: hit.cover_is_nsfw,
    is_nsfw: hit.is_nsfw,
    visibility: hit.visibility,
    moderation_status: hit.moderation_status,
    moderation_report_status: hit.moderation_report_status,
    status: hit.status,
    tags: rulebook?.tags,
    characterCardVariant: CharacterCardVariant.DEFAULT,
    uniqueKey,
    isHidden: false,
    characterCardActionsHandler: (action: CharacterCardActions | CharacterCardDropdownActions, charId: string) => {
      characterCardActionsHandler(action, charId, hit.is_nsfw);
    },
    dropdownActions: getDropdownActions({
      t,
      characterCardActionsHandler,
      isAuthenticated,
      id,
      collection: Collection.RULEBOOKS,
      canClone:
        hit.definition_visible === true && isRulebookDraftReady === true && applicationCapabilities.isAllowed(SubCapabilityKey.RulebookManage),
      canAttach,
    }),
  };
}
